import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { AutoStories as StarBookIcon, QuestionAnswer as QuestionAnswerIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { getChartSummary, ChartSummaryResponse } from '../../api/qa';

interface ChartSummaryCardProps {
  chartTip?: string;
  recommendedQuestionId?: string;
  recommendedQuestionText?: string;
}

const STAR_COLORS: Record<string, string> = {
  紫薇: '#9c27b0',
  天機: '#2196f3',
  太陽: '#ff9800',
  武曲: '#607d8b',
  天同: '#4caf50',
  廉貞: '#f44336',
  天府: '#795548',
  太陰: '#5c6bc0',
  貪狼: '#e91e63',
  巨門: '#00897b',
  天相: '#43a047',
  天梁: '#1565c0',
  七殺: '#c62828',
  破軍: '#4527a0',
};

const ChartSummaryCard: React.FC<ChartSummaryCardProps> = ({ chartTip, recommendedQuestionId, recommendedQuestionText }) => {
  const [data, setData] = useState<ChartSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getChartSummary().then(res => {
      setData(res);
      setLoading(false);
    });
  }, []);

  const starColor = data?.display_star ? (STAR_COLORS[data.display_star] ?? '#7e57c2') : '#7e57c2';

  return (
    <Card elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ bgcolor: starColor }}>
            <StarBookIcon />
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            命宮主星特色
          </Typography>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && data?.success && data.display_star && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={Array.isArray(data.main_stars) && data.main_stars.length > 1
                  ? data.main_stars.join(' + ')
                  : String(data.display_star ?? '')}
                sx={{ bgcolor: starColor, color: '#fff', fontWeight: 'bold', fontSize: '1rem', px: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                命宮主星
              </Typography>
            </Box>

            {data.description && (
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
                {String(data.description)}
              </Typography>
            )}

            {data.tip && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ bgcolor: '#fff8e1', borderLeft: '3px solid #ff9800', pl: 1.5, py: 0.5, borderRadius: '0 4px 4px 0' }}>
                  <Typography variant="body2" color="warning.dark" fontWeight="bold">
                    提醒
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {String(data.tip)}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}

        {!loading && data?.success && !data.display_star && (
          <Typography variant="body2" color="text.secondary">
            命宮為空宮，運勢由對宮主星影響，整體較具彈性與可塑性。
          </Typography>
        )}

        {chartTip && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ bgcolor: '#e3f2fd', borderLeft: '3px solid #1976d2', pl: 1.5, py: 1, pr: 1.5, borderRadius: '0 4px 4px 0' }}>
              <Typography variant="body2" color="primary.dark" fontWeight="bold" gutterBottom>
                本月命盤提示
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: recommendedQuestionId ? 1 : 0 }}>
                {chartTip}
              </Typography>
              {recommendedQuestionId && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(`/questions/${recommendedQuestionId}`)}
                  sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.8rem' }}
                >
                  今日推薦：{recommendedQuestionText}
                </Button>
              )}
            </Box>
          </>
        )}

        {!loading && data?.success && (
          <Box mt={2}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<QuestionAnswerIcon />}
              onClick={() => navigate('/questions')}
              sx={{ textTransform: 'none' }}
            >
              前往問問題，深入了解你的命盤
            </Button>
          </Box>
        )}

        {!loading && !data?.success && (
          <Typography variant="body2" color="text.secondary">
            無法載入命盤資料，請稍後再試。
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ChartSummaryCard;
